/**
 * SKU category tree for the Wokiee collection sidebar.
 *
 * Renders assets/sku-filter-tree.json into Wokiee's own `tt-collapse` sidebar markup and drives the
 * native `filter.p.tag` facet, so the taxonomy, the tag strings and the selection rules match the
 * Ella build running on DirectRC and HDRC.
 *
 * Three things shape this file:
 *
 * 1. Shopify ORs repeated `filter.p.tag` values — measured on this store, not assumed:
 *    `rcf2|Motors` returns 24, `rcf2|ESC` returns 39, and both together return 63, the union. An
 *    earlier version of this file was written believing they were ANDed, and so submitted the
 *    level 2 tag alongside the level 3 one; because a level 3 category is a subset of its parent,
 *    the union came back equal to the parent and picking a sub-row narrowed nothing at all
 *    (`rcf2|ESC` 39, `rcf3|Brushed ESC` 7, both together 39). A selection therefore submits exactly
 *    one tag: the deepest level chosen. Only one tag per level is kept for the same reason — two
 *    selected siblings would widen the result set rather than narrow it.
 * 2. The tree is data, not DOM Wokiee owns. `collections-filtres.js` swaps `.shopifyfiltres-js`
 *    lists by index between the current page and the AJAX response, so this list uses its own
 *    `skufiltres-js` class and re-renders itself on `listingpageajax` — the event that script fires
 *    at the end of every swap — plus `popstate` for back/forward.
 * 3. Rows are plain links that navigate. Wokiee's AJAX path only re-renders `.tt-product-listing`
 *    and the filter lists it recognises; a full load keeps the sidebar, the toolbar and the product
 *    count consistent for the price of one request.
 */
(function () {
  'use strict';

  var TAG_PARAM = 'filter.p.tag';
  var ROOT = '[data-sku-category-sidebar]';
  // theme.js's ttCollapse() slides its groups with `var speed = 300`; matching it keeps the two
  // sets of sidebar groups feeling like one control.
  var SLIDE_MS = 300;

  var treePromises = {};
  var availPromises = {};

  function loadTree(url) {
    if (!treePromises[url]) {
      treePromises[url] = fetch(url)
        .then(function (res) {
          if (!res.ok) throw new Error('Failed to load category tree');
          return res.json();
        })
        .then(function (data) {
          return data.tree || {};
        });
    }
    return treePromises[url];
  }

  /**
   * The set of category tags a collection actually contains, or null when it cannot be known.
   *
   * Null means "show everything": that is the case on a search results page, whose result set is
   * arbitrary and so is not in the precomputed asset. Hiding rows on a guess would strand a shopper
   * whose selection had simply not been anticipated.
   */
  function loadAvailability(url, handle) {
    if (!url || !handle) return Promise.resolve(null);
    if (!availPromises[url]) {
      availPromises[url] = fetch(url)
        .then(function (res) { return res.ok ? res.json() : null; })
        .catch(function () { return null; });
    }
    return availPromises[url].then(function (data) {
      if (!data || !data.collections || !data.collections[handle]) return null;
      var tags = data.tags || [];
      var set = {};
      data.collections[handle].forEach(function (i) { set[tags[i]] = true; });
      return set;
    });
  }

  function tag(level, value) {
    return 'rcf' + level + '|' + value;
  }

  /** The tags currently in the URL, keyed by level, so the tree can show what is selected. */
  function activeTags() {
    var active = {};
    new URLSearchParams(window.location.search).getAll(TAG_PARAM).forEach(function (value) {
      var match = value.match(/^rcf([1-4])\|(.+)$/);
      if (match) active[match[1]] = match[2];
    });
    return active;
  }

  /**
   * Fill in the level 2 that owns an active level 3.
   *
   * A level 3 selection puts only its own tag in the URL, so activeTags() sees no level 2 — yet the
   * sidebar needs one to know which group to open and which parent row to expand. The tree supplies
   * it: level 3 names are unique across the whole tree (28 of them, none repeated under a second
   * level 2, none colliding with a level 2 name), so the lookup is unambiguous. Should that ever
   * stop holding, the first match wins and the wrong group would open — worth re-checking whenever
   * the tree is regenerated.
   */
  function resolveActive(tree, active) {
    if (!active['3'] || active['2']) return active;
    Object.keys(tree).some(function (l1) {
      return Object.keys(tree[l1] || {}).some(function (l2) {
        if (Object.keys(tree[l1][l2] || {}).indexOf(active['3']) === -1) return false;
        active['2'] = l2;
        return true;
      });
    });
    return active;
  }

  /**
   * URL for the page with `selections` applied. Everything else in the query string survives —
   * sort_by, availability, price, the search terms — but paging never does: the target page holds a
   * different number of products, so any retained `page` would likely land past the end of it.
   */
  function buildUrl(baseUrl, selections) {
    var onSearchPage = window.location.pathname.indexOf('/search') === 0;
    var url = new URL(onSearchPage ? window.location.pathname : baseUrl, window.location.origin);
    var current = new URLSearchParams(window.location.search);
    var next = new URLSearchParams();

    current.forEach(function (value, key) {
      if (key === TAG_PARAM || key === 'page') return;
      next.append(key, value);
    });

    Object.keys(selections).forEach(function (level) {
      if (selections[level]) next.append(TAG_PARAM, tag(level, selections[level]));
    });

    url.search = next.toString();
    return url.pathname + (url.search ? url.search : '');
  }

  function el(tagName, className, text) {
    var node = document.createElement(tagName);
    if (className) node.className = className;
    if (text != null) node.textContent = text;
    return node;
  }

  /**
   * One `tt-collapse` block per level 1 category, matching what collection-sidebar-filter-x2.liquid
   * emits so Wokiee's own collapse/scroll behaviour applies without extra CSS.
   */
  function renderGroup(l1, l2map, state, config) {
    var active = state.active;
    var avail = state.avail;

    /** A row is worth showing if the collection has products under it — or if it is the current
     *  selection, which must stay clickable so it can be cleared. */
    function keep(level, value) {
      if (!avail) return true;
      if (active[String(level)] === value) return true;
      return !!avail[tag(level, value)];
    }
    var group = el('div', 'tt-collapse sku-category-collapse');
    // Level 1 is a heading, never a facet value — same as the Ella build, where only the level 2 and
    // level 3 rows are checkboxes. Submitting the level 1 tag as well would AND a third value onto
    // every query for no extra narrowing.
    var isOpen = Object.keys(l2map).some(function (l2) {
      return active['2'] === l2;
    });
    if (isOpen) group.classList.add('open');

    // Deliberately NOT `tt-collapse-title` / `tt-collapse-content`. theme.js's ttCollapse() binds a
    // click handler straight onto every `.tt-collapse-title` it can see and slideUp()s the matching
    // `.tt-collapse-content`. When this markup carried those classes both handlers fired on one
    // click: ours added `open`, Wokiee's then saw `open`, removed it and slid the panel back up, so
    // the group never opened. Own class names keep Wokiee out of this subtree entirely; the wrapper
    // keeps `tt-collapse` because its `position: relative` anchors the +/- icon and its
    // `:not(:last-child)` margin spaces the groups. component-sku-category-filter.css restates the
    // title and panel styles so the look is unchanged.
    // A div, not the <h3> Wokiee uses for its own filter titles. theme.css resets headings with
    // `h3:not([class^="tt-title"]):not([class^="tt-collapse"]):not([class^="tt-aside"])`, which at
    // specificity 0,3,1 outweighs any single-class rule — Wokiee only escapes it because its class
    // literally starts with `tt-collapse`. Not being an h3 sidesteps the cascade entirely, and since
    // this is a control rather than page content, role=button + tabindex also makes it reachable by
    // keyboard, which a clickable heading would not be.
    var titleClass = 'sku-category-collapse__title';
    if (config.uppercase) titleClass += ' text-uppercase';
    var title = el('div', titleClass, l1);
    title.setAttribute('role', 'button');
    title.setAttribute('tabindex', '0');
    title.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
    group.appendChild(title);

    var content = el('div', 'sku-category-collapse__content');
    // Groups holding the active selection start expanded. Set inline so it matches what the slide
    // animation leaves behind, and so nothing has to flash open before being hidden again.
    if (isOpen) content.style.display = 'block';
    var list = el('ul', 'tt-list-row skufiltres-js sku-category-tree__list');

    Object.keys(l2map).sort(compare).forEach(function (l2) {
      if (!keep(2, l2)) return;
      var item = el('li', 'sku-category-tree__item sku-category-tree__item--l2');
      var selected = active['2'] === l2;
      if (selected) item.classList.add('is-active');

      // Re-clicking the selected row clears it; that is the only way back out of a level with no
      // checkbox to untick. Clearing a level 2 drops its level 3 with it.
      item.appendChild(link(l2, buildUrl(config.baseUrl, selected ? {} : { 2: l2 }), selected));
      // Note the level 3 links below submit only their own tag. Adding the parent's tag as well
      // would OR the two and hand back the parent's whole result set — see the note at the top.

      var l3map = l2map[l2] || {};
      var l3keys = Object.keys(l3map).sort(compare).filter(function (l3) {
        return keep(3, l3);
      });
      if (selected && l3keys.length) {
        var sub = el('ul', 'tt-list-row sku-category-tree__list sku-category-tree__list--l3');
        l3keys.forEach(function (l3) {
          var l3selected = active['3'] === l3;
          var row = el('li', 'sku-category-tree__item sku-category-tree__item--l3');
          if (l3selected) row.classList.add('is-active');
          row.appendChild(link(l3, buildUrl(config.baseUrl, l3selected
            ? { 2: l2 }
            : { 3: l3 }), l3selected));
          sub.appendChild(row);
        });
        item.appendChild(sub);
      }

      list.appendChild(item);
    });

    if (!list.childNodes.length) return null; // every row filtered out — no empty heading
    content.appendChild(list);
    group.appendChild(content);
    return group;
  }

  function link(label, href, selected) {
    var a = el('a', null, label);
    a.href = href;
    if (selected) {
      a.classList.add('is-active');
      a.setAttribute('aria-current', 'true');
    }
    return a;
  }

  function compare(a, b) {
    return a.localeCompare(b);
  }

  function render(root, tree, avail) {
    var config = {
      baseUrl: root.dataset.collectionUrl || '/collections/all',
      uppercase: root.dataset.titlesUppercase === 'true'
    };
    var state = { active: resolveActive(tree, activeTags()), avail: avail };

    root.innerHTML = '';

    // A level 1 is only worth a block when it actually holds subcategories.
    Object.keys(tree).forEach(function (l1) {
      var l2map = tree[l1] || {};
      if (!Object.keys(l2map).length) return;
      var group = renderGroup(l1, l2map, state, config);
      if (group) root.appendChild(group);
    });

    if (state.active['2'] || state.active['3']) {
      var clear = el('div', 'sku-category-tree__clear');
      clear.appendChild(link(root.dataset.clearLabel || 'CLEAR ALL', buildUrl(config.baseUrl, {}), false));
      root.appendChild(clear);
    }

    // Wokiee binds its collapse behaviour on load, so freshly injected headings need it applied here.
    bindCollapse(root);
  }

  /**
   * Open/close is driven purely by the `open` class, which the stylesheet turns into
   * `display: block` on the panel and flips the +/- glyph. Wokiee's own collapse animates with
   * jQuery inline styles instead; keeping this class-only avoids inline `display` left behind by
   * one mechanism overriding the other.
   */
  function bindCollapse(root) {
    var $ = window.jQuery;
    root.querySelectorAll('.sku-category-collapse__title').forEach(function (title) {
      function toggle() {
        var group = title.closest('.sku-category-collapse');
        var panel = group.querySelector('.sku-category-collapse__content');
        var open = !group.classList.contains('open');
        group.classList.toggle('open', open);
        title.setAttribute('aria-expanded', open ? 'true' : 'false');

        if (!panel) return;
        // Animate through jQuery at theme.js's own 300ms so the motion is indistinguishable from the
        // Availability / Price groups below, which ttCollapse() slides with the same call. Nothing in
        // CSS may key visibility off `.open`: the class flips before this runs, which would leave the
        // panel already at full height and make slideDown() a no-op.
        if (!$) {
          panel.style.display = open ? 'block' : 'none';
          return;
        }
        var $panel = $(panel).stop(true, true);
        if (open) {
          $panel.slideDown(SLIDE_MS);
        } else {
          $panel.slideUp(SLIDE_MS);
        }
      }
      title.addEventListener('click', toggle);
      title.addEventListener('keydown', function (event) {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault(); // Space would otherwise scroll the page
        toggle();
      });
    });
  }

  function paint() {
    var roots = document.querySelectorAll(ROOT);
    if (!roots.length) return;
    roots.forEach(function (root) {
      var url = root.dataset.treeUrl;
      if (!url) return;
      // Both are cached per URL, so the pair costs one request each for the whole session.
      Promise.all([
        loadTree(url),
        loadAvailability(root.dataset.availabilityUrl, root.dataset.collectionHandle)
      ])
        .then(function (out) {
          render(root, out[0], out[1]);
        })
        .catch(function () {
          root.innerHTML = '';
          root.appendChild(el('p', 'sku-category-tree__error', 'Category list unavailable.'));
        });
    });
  }

  function init() {
    paint();
    // collections-filtres.js fires this on <body> after it has swapped in an AJAX response.
    if (window.jQuery) window.jQuery('body').on('listingpageajax', paint);
    window.addEventListener('popstate', paint);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
